import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '../config/constants';
import Modal from './Modal';

interface Vendor {
  _id: string;
  producerId: string;
  name: string;
  companyName: string;
  email: string;
  phone: string;
  address: string;
  note?: string;
}

interface VendorSelectorProps {
  selectedVendor: Vendor | null;
  onVendorSelect: (vendor: Vendor) => void;
}

const VendorSelector: React.FC<VendorSelectorProps> = ({ selectedVendor, onVendorSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [filteredVendors, setFilteredVendors] = useState<Vendor[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newVendor, setNewVendor] = useState<Partial<Vendor>>({});
  const [modal, setModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const selectorRef = useRef<HTMLDivElement>(null);

  const fetchVendors = async () => {
    try {
      const response = await fetch(`${API_URL}/vendors`);
      if (!response.ok) throw new Error('Üreticiler yüklenemedi');
      const data = await response.json();
      setVendors(data);
      setFilteredVendors(data);
    } catch (error) {
      console.error('Üreticiler yüklenirken hata oluştu:', error);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    const filtered = vendors.filter(vendor =>
      vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.producerId.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredVendors(filtered);
  }, [searchTerm, vendors]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddVendor = async () => {
    try {
      // Veri doğrulama
      const requiredFields = ['name', 'companyName', 'email', 'phone', 'address'];
      const missingFields = requiredFields.filter(field => !newVendor[field as keyof Vendor]);
      
      if (missingFields.length > 0) {
        setModal({
          isOpen: true,
          title: 'Eksik Bilgi',
          message: 'Lütfen tüm zorunlu alanları doldurun.',
          type: 'error'
        });
        return;
      }

      const response = await fetch(`${API_URL}/vendors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(newVendor)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Üretici eklenirken bir hata oluştu');
      }

      await fetchVendors();
      setIsAddModalOpen(false);
      setNewVendor({});
      onVendorSelect(data); // Yeni eklenen üreticiyi otomatik seç
      setModal({
        isOpen: true,
        title: 'Başarılı',
        message: 'Üretici başarıyla eklendi',
        type: 'success'
      });
    } catch (error) {
      console.error('Üretici eklenirken hata:', error);
      setModal({
        isOpen: true,
        title: 'Hata',
        message: error instanceof Error ? error.message : 'Üretici eklenirken bir hata oluştu',
        type: 'error'
      });
    }
  };

  return (
    <div className="relative" ref={selectorRef}>
      {/* Seçici Butonu */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-left flex justify-between items-center focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <span className="text-gray-700">
          {selectedVendor 
            ? `${selectedVendor.producerId} - ${selectedVendor.companyName}`
            : 'Üretici Seçin'}
        </span>
        <svg className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menü */}
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
          <div className="p-2 border-b">
            <div className="relative">
              <input
                type="text"
                placeholder="Üretici Ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <svg className="absolute left-2 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          <div className="max-h-60 overflow-y-auto">
            {filteredVendors.map(vendor => (
              <button
                key={vendor._id}
                type="button"
                onClick={() => {
                  onVendorSelect(vendor);
                  setIsOpen(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
              >
                <div className="font-medium">{vendor.producerId} - {vendor.companyName}</div>
                <div className="text-sm text-gray-500">{vendor.name}</div>
              </button>
            ))}
          </div>

          <div className="p-2 border-t">
            <button
              type="button"
              onClick={() => {
                setIsAddModalOpen(true);
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-center text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              + Yeni Üretici Ekle
            </button>
          </div>
        </div>
      )}

      {/* Yeni Üretici Ekleme Modalı */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setNewVendor({});
        }}
        title="Yeni Üretici Ekle"
        type="info"
        message=""
        closeOnClickOutside={true}
        buttons={[
          {
            text: 'İptal',
            onClick: () => {
              setIsAddModalOpen(false);
              setNewVendor({});
            },
            className: 'px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
          },
          {
            text: 'Ekle',
            onClick: handleAddVendor,
            className: 'px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700'
          }
        ]}
      >
        <div className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Firma Adı</label>
            <input
              type="text"
              value={newVendor.companyName || ''}
              onChange={(e) => setNewVendor(prev => ({ ...prev, companyName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Yetkili Adı</label>
            <input
              type="text"
              value={newVendor.name || ''}
              onChange={(e) => setNewVendor(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-posta</label>
            <input
              type="email"
              value={newVendor.email || ''}
              onChange={(e) => setNewVendor(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
            <input
              type="tel"
              value={newVendor.phone || ''}
              onChange={(e) => setNewVendor(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Adres</label>
            <textarea
              value={newVendor.address || ''}
              onChange={(e) => setNewVendor(prev => ({ ...prev, address: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Not</label>
            <textarea
              value={newVendor.note || ''}
              onChange={(e) => setNewVendor(prev => ({ ...prev, note: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              rows={2}
            />
          </div>
        </div>
      </Modal>

      {/* Bilgi/Hata Modalı */}
      <Modal
        isOpen={modal.isOpen}
        onClose={() => setModal(prev => ({ ...prev, isOpen: false }))}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
    </div>
  );
};

export default VendorSelector; 